import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { NewSite } from 'features/Sites/components/NewSite'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { EDIT_SITE } from 'features/Sites/mutations-legacy'
import { GET_SITE } from 'features/Sites/queries-legacy'
import { getConfirmationModalities } from 'resolvers/payment'
import { useLogin } from 'features/Login/hooks'
import { getDifference, filterEmptyValue } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { IFormikSite } from 'features/Sites/components/NewSite/types'
import { ISitePayload } from '../New/types'

const messages = defineMessages({
  helmet: {
    id: 'sites.edit.helmet',
    defaultMessage: 'Edit Point of Sale | Nexbus Admin'
  }
})

const EditSiteView = (): ReactElement => {
  const navigate = useNavigate()
  const { siteId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const [editSite, { loading: mutationLoading, error: mutationError }] = useMutation(EDIT_SITE, {
    onCompleted() {
      navigate('/sites')
    }
  })

  const handleSubmit = (site: IFormikSite): void => {
    const previousSite = {
      name: queryData.site.name,
      type: queryData.site.type,
      companyID: queryData.site.company.id,
      userIDs: queryData.site.users.map(({ id }: { id: string }) => id),
      address: !isEmpty(queryData.site.address)
        ? {
            line1: queryData.site.address.line1 || '',
            line2: queryData.site.address.line2 || '',
            city: queryData.site.address.city || '',
            stateCode: queryData.site.address.stateCode || '',
            countryCode: queryData.site.address.countryCode || '',
            zipCode: queryData.site.address.zipCode || ''
          }
        : null,
      settings: {
        snacks: {
          canSell: queryData.site.settings.snacks ? queryData.site.settings.snacks.canSell : false,
          showMinPrice: queryData.site.settings.snacks ? queryData.site.settings.snacks.showMinPrice : false,
          tripRequired: queryData.site.settings.snacks ? queryData.site.settings.snacks.tripRequired : false
        },
        kredits: {
          canTopUp: queryData.site.settings.kredits ? queryData.site.settings.kredits.canTopUp : false
        },
        defaultOriginID: queryData.site.settings.defaultOrigin.id,
        passengerNameRequired: queryData.site.settings.passengerNameRequired,
        hasPrinter: queryData.site.settings.hasPrinter,
        canChangePrice: queryData.site.settings.canChangePrice,
        saleClosingThreshold: queryData.site.settings.saleClosingThreshold,
        payment: queryData.site.settings.payment,
        enabledCompanyLineIDs: queryData.site.settings.enabledCompanyLines.map(({ id }: { id: string }) => id),
        canReserveTickets: queryData.site.settings.canReserveTickets,
        enableMixedPayments: queryData.site.settings.enableMixedPayments,
        hideTransactionsReport: queryData.site.settings.hideTransactionsReport
      }
    }
    const [paymentsPayload, canReserveTickets, enableMixedPayments] = getConfirmationModalities(site.settings.payment)
    const emptyAddressHandler = {
      line1: '',
      line2: '',
      city: '',
      stateCode: '',
      countryCode: '',
      zipCode: ''
    }
    const newSite: ISitePayload = {
      name: site.name,
      type: site.type,
      companyID: site.company.id,
      userIDs: site.users.map(({ id }) => id),
      address: !isEmpty(filterEmptyValue(site.address)) ? site.address : emptyAddressHandler,
      settings: {
        snacks: {
          canSell: site.settings.snacks.canSell,
          showMinPrice: site.settings.snacks.showMinPrice,
          tripRequired: site.settings.snacks.tripRequired
        },
        kredits: {
          canTopUp: site.settings.kredits.canTopUp
        },
        defaultOriginID: site.settings.defaultOrigin.id,
        passengerNameRequired: site.settings.passengerNameRequired,
        hasPrinter: site.settings.hasPrinter,
        canChangePrice: site.settings.canChangePrice,
        saleClosingThreshold: site.settings.saleClosingThreshold,
        payment: paymentsPayload.map((method) => method),
        enabledCompanyLineIDs: site.settings.enabledCompanyLineIDs,
        canReserveTickets,
        enableMixedPayments,
        hideTransactionsReport: site.settings.hideTransactionsReport
      }
    }

    const isAddingAddress =
      !(previousSite.address && previousSite.address.line1) && newSite.address && newSite.address.line1
    if (isAddingAddress && newSite.address) {
      newSite.address.countryCode = 'MX'
    }

    const payload = getDifference(newSite, previousSite) as ISitePayload

    if (!newSite.address && previousSite.address) {
      payload.address = null
    }

    if (isEmpty(payload)) {
      navigate('/sites')
    } else {
      editSite({
        variables: {
          site: {
            id: siteId,
            ...payload
          }
        }
      })
    }
  }

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_SITE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      siteId
    }
  })

  useEffect(() => {
    refetch()
  }, [])

  if (!canWrite(FEATURES.SITE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SITE} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="sites.edit.error.description"
              defaultMessage="There was an error while loading your point of sale"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="sites.edit.error.action" defaultMessage="Reload your point of sale" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewSite onSubmit={handleSubmit} site={queryData.site} loading={mutationLoading} error={mutationError} isEdit />
    )
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="sites.edit.title" defaultMessage="Edit point of sale" />
        </Heading.H3>
      </ContentHeader>

      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditSiteView)
